import React, { useState, useEffect } from 'react';
import { Search, Loader2, CheckCircle, XCircle, Eye, Edit2, X, Wifi, MapPin, Settings, ArrowRight } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { Helmet } from "react-helmet-async";
import operatorKitService from '@/api/services/operatorKitService';
import type { Kit, UpdateKitData } from '@/api/services/operatorKitService';
import { useTranslation } from "react-i18next";
import { AxiosError } from 'axios';
import { getErrorMessage } from '@/utils/errorUtils';

export default function AllKitsPage() {
  const navigate = useNavigate();
  const { theme } = useApp();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'pending' | 'disabled'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [kits, setKits] = useState<Kit[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 1,
  });

  // Modal states
  const [viewModal, setViewModal] = useState<Kit | null>(null);
  const [editModal, setEditModal] = useState<Kit | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [editSuccess, setEditSuccess] = useState(false);

  const [editForm, setEditForm] = useState<UpdateKitData>({
    kitType: 'basic',
    quantity: 1,
    deploymentLocation: '',
  });



  // Refetch when tab or page changes (immediate)
  useEffect(() => {
    fetchKits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pagination.page]);

  // Debounce searchQuery 400ms so typing doesn't hammer the API
  useEffect(() => {
    const handle = window.setTimeout(() => {
      fetchKits();
    }, 400);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchKits = async () => {
    try {
      setLoading(true);
      const response = await operatorKitService.getAllKits({
        status: activeTab,
        search: searchQuery || undefined,
        page: pagination.page,
        limit: pagination.limit,
      });
      setKits(response.kits);
      setPagination(response.pagination);
    } catch (error: unknown) {
      console.error('🔴 [AllKits] Error fetching kits:', error);
      console.error('🔴 [AllKits] Error details:', error instanceof AxiosError ? error.response?.data : error);
      showMessage('error', getErrorMessage(error) || t('operator.allKitsPage.messages.loadFailed', { defaultValue: 'Failed to load kits' }));
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleTabChange = (tab: 'all' | 'active' | 'pending' | 'disabled') => {
    setActiveTab(tab);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // View kit details
  const handleViewKit = async (kitId: string) => {
    // Fast path: if we already have this kit in our list, show instantly.
    // The list endpoint returns full kit objects already; an extra GET is wasteful.
    const existing = kits.find((k) => (k.id || k._id) === kitId);
    if (existing) {
      setViewModal(existing);
      return;
    }
    // Fallback: lookup by id (deep-link, page change, etc.)
    try {
      setActionLoading(true);
      const response = await operatorKitService.getKitById(kitId);
      const kitResponse = response as unknown as Record<string, unknown>;
      const kitData = (kitResponse.kit || kitResponse.data || response) as Kit;
      setViewModal(kitData);
    } catch (error: unknown) {
      console.error('Error fetching kit details:', error);
      showMessage('error', getErrorMessage(error) || t('operator.allKitsPage.messages.loadDetailsFailed', { defaultValue: 'Failed to load kit details' }));
    } finally {
      setActionLoading(false);
    }
  };

const handleEditKit = (kit: Kit) => {
  let loc = "";
  if (kit.location) {
    loc = typeof kit.location === 'string' ? kit.location : (typeof kit.location === "object" ? kit.location?.address : kit.location);
  }
  if (loc === "Not set") loc = "";

  setEditForm({
    kitType: kit.kitType || (kit.name?.toLowerCase().includes('basic') ? 'basic' : 'advanced'),
    quantity: kit.quantity || 1,
    deploymentLocation: loc,
  });
  
  setEditModal(kit);
  // setEditStep(1);
  setEditSuccess(false);
};

  const updateEditField = (field: keyof UpdateKitData, value: string | number) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };


const handleUpdateKit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!editModal) return;

  try {
    setActionLoading(true);

    const payload: UpdateKitData = {
      kitType: editForm.kitType,
      quantity: Number(editForm.quantity),
      deploymentLocation: editForm.deploymentLocation
    };

    // 1. Wait for the server to finish saving
    await operatorKitService.updateKit(editModal.id, payload);
    
    // 2. Wait for the fresh data to be fetched from the database
    await fetchKits();
    
    showMessage('success', t('operator.allKitsPage.messages.kitUpdated', { defaultValue: 'Kit updated successfully!' }));
    setEditSuccess(true);
    
    setTimeout(() => {
      setEditModal(null);
      setEditSuccess(false);
    }, 1500);

  } catch (error: unknown) {
    showMessage('error', getErrorMessage(error) || t('operator.allKitsPage.messages.updateFailed', { defaultValue: 'Failed to update' }));
  } finally {
    setActionLoading(false);
  }
};

  const getStatusColor = (status: string) => {
    switch(status.toLowerCase()) {
      case 'active': return 'text-green-600 bg-green-50 dark:bg-green-900/30 dark:text-green-400';
      case 'pending': return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'disabled': return 'text-red-600 bg-red-50 dark:bg-red-900/30 dark:text-red-400';
      default: return 'text-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  // Helper function to format dates safely
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return t('operator.allKitsPage.common.na', { defaultValue: 'N/A' });
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return t('operator.allKitsPage.common.na', { defaultValue: 'N/A' });
      return date.toLocaleDateString('en-US', { 
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error: unknown) {
      console.error('Date formatting error:', error);
      return t('operator.allKitsPage.common.na', { defaultValue: 'N/A' });
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('operator.allKitsPage.meta.title', { defaultValue: 'All Kits | KonectX' })}</title>
      </Helmet>

      <div className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'}`}>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Global Message */}
          {message && (
            <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
            }`}>
              {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              <span className="text-sm">{message.text}</span>
            </div>
          )}

          {/* Title and Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-0 mb-6">
            <h1 className={`text-2xl sm:text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{t('operator.allKitsPage.title', { defaultValue: 'My Kits' })}</h1>
            <div className="flex w-full sm:w-auto gap-3">
              <button
                onClick={() => navigate("/operator/registerKit")}
                className="flex-1 sm:flex-none px-5 py-2.5 rounded-md bg-[#2E5A88] hover:bg-[#244a73] text-white font-semibold transition-colors flex items-center justify-center gap-2"
              >
                {t('operator.allKitsPage.actions.addKit', { defaultValue: 'Register New Kit' })}
                <ArrowRight size={16} />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} size={20} />
              <input
                type="text"
                placeholder={t('operator.allKitsPage.searchPlaceholder', { defaultValue: 'Search Kits' })}
                value={searchQuery}
                onChange={handleSearch}
                className={`w-full pl-10 pr-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2E5A88]/30 focus:border-[#2E5A88] transition ${
                  theme === 'dark' ? 'bg-[#333436] border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>
          </div>

          {/* Tabs */}
          <div className={`flex gap-2 sm:gap-6 mb-6 border-b overflow-x-auto whitespace-nowrap hide-scrollbar ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
            {(['all', 'active', 'pending', 'disabled'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`px-3 py-3 text-sm font-semibold transition-colors relative ${
                  activeTab === tab
                    ? 'text-[#2E5A88] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-[#2E5A88]'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-600 hover:text-gray-900'
                }`}>
                {t(`operator.allKitsPage.tabs.${tab}`, {
                  defaultValue:
                    tab === 'all' ? 'All Kits' :
                    tab === 'active' ? 'Active' :
                    tab === 'pending' ? 'Pending' :
                    'Disabled',
                })}
              </button>
            ))}
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#2E5A88]" />
            </div>
          ) : (
            <div className={`rounded-lg border overflow-hidden ${theme === 'dark' ? 'bg-[#333436] border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
              <div className="overflow-x-auto">
  <table className="w-full">
    <thead className={theme === 'dark' ? 'bg-[#2b2b2c]' : 'bg-gray-50'}>
      <tr>
        {[t('operator.allKitsPage.columns.kitName', { defaultValue: 'Kit Name' }), t('operator.allKitsPage.columns.status', { defaultValue: 'Status' }), t('operator.allKitsPage.columns.activationDate', { defaultValue: 'Activation Date' }), t('operator.allKitsPage.columns.location', { defaultValue: 'Location' }), t('operator.allKitsPage.columns.actions', { defaultValue: 'Actions' })].map((header) => (
          <th key={header} className={`px-6 py-3 text-left text-sm font-bold uppercase tracking-wider ${
            theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
          }`}>{header}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {kits.map((kit) => {
        // Resolve Location: Try deploymentLocation, then object address, then raw string
        const displayLocation = kit.deploymentLocation || 
          (typeof kit.location === 'object' ? kit.location?.address : kit.location) || 
          t('operator.allKitsPage.common.notSet', { defaultValue: 'Not set' });

        // Resolve Date: Try activationDate first, then createdAt timestamp
        const displayDate = kit.activationDate || kit.createdAt;

        return (
          <tr key={kit.id || kit._id} className={`border-b ${theme === 'dark' ? 'border-gray-700 hover:bg-[#3a3b3d]' : 'border-gray-200 hover:bg-gray-50'}`}>
            <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {kit.name || t('operator.allKitsPage.common.unnamedKit', { defaultValue: 'Unnamed Kit' })}
            </td>
            
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(kit.status)}`}>
                {(kit.status || 'pending').toUpperCase()}
              </span>
            </td>

            <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              {displayDate ? formatDate(displayDate) : t('operator.allKitsPage.common.pending', { defaultValue: 'Pending' })}
            </td>

            <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              {displayLocation}
            </td>

            <td className="px-6 py-4 whitespace-nowrap text-sm">
              <div className="flex gap-2">
                <button
                  onClick={() => handleViewKit(kit.id || kit._id)}
                  aria-label={t('operator.allKitsPage.actions.view', { defaultValue: 'View kit' })}
                  className="p-2 text-[#2E5A88] hover:bg-[#2E5A88]/10 rounded-md transition-colors"
                >
                  <Eye size={18} />
                </button>
                <button
                  onClick={() => handleEditKit(kit)}
                  aria-label={t('operator.allKitsPage.actions.edit', { defaultValue: 'Edit kit' })}
                  className="p-2 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-md transition-colors"
                >
                  <Edit2 size={18} />
                </button>
              </div>
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
</div>
              {kits.length === 0 && (
                <div className={`text-center py-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t('operator.allKitsPage.noKitsFound', { defaultValue: 'No kits found matching your search criteria.' })}
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {!loading && pagination.pages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('operator.allKitsPage.pagination.showing', {
                  defaultValue: 'Showing page {{page}} of {{pages}} ({{total}} total)',
                  page: pagination.page,
                  pages: pagination.pages,
                  total: pagination.total,
                })}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    theme === 'dark'
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {t('operator.allKitsPage.actions.previous', { defaultValue: 'Previous' })}
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 rounded-lg font-semibold text-sm bg-[#2E5A88] hover:bg-[#244a73] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('operator.allKitsPage.actions.next', { defaultValue: 'Next' })}
                </button>
              </div>
            </div>
          )}
        </main>



{viewModal && (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-in fade-in duration-200">
    <div className={`rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 ${
      theme === 'dark' ? 'bg-[#1a1a1a] border border-gray-800' : 'bg-white'
    }`}>
      
      {/* Modal Header */}
      <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
        <div>
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {viewModal.name || t('operator.allKitsPage.view.kitDetails', { defaultValue: 'Kit Details' })}
          </h2>
          <p className="text-sm text-gray-500 mt-1">ID: {viewModal.id || viewModal._id}</p>
        </div>
        <button 
          onClick={() => setViewModal(null)} 
          className={`p-2 rounded-xl transition ${theme === 'dark' ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
        >
          <X size={24} />
        </button>
      </div>
      
      {/* Modal Content */}
      <div className="p-4 sm:p-8 space-y-6 sm:space-y-8 overflow-y-auto max-h-[calc(90vh-100px)]">
        
        {/* Top Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
             <label className="text-xs font-bold uppercase tracking-wider text-gray-500 block mb-1">{t('operator.allKitsPage.columns.status', { defaultValue: 'Status' })}</label>
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(viewModal.status || '')}`}>
              {viewModal.status?.toUpperCase() || t('operator.allKitsPage.common.unknown', { defaultValue: 'UNKNOWN' })}
            </span>
          </div>
          
          <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
             <label className="text-xs font-bold uppercase tracking-wider text-gray-500 block mb-1">{t('operator.allKitsPage.view.kitType', { defaultValue: 'Kit Type' })}</label>
            <p className={`text-lg font-semibold capitalize ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {viewModal.kitType || t('operator.allKitsPage.view.basic', { defaultValue: 'Basic' })}
            </p>
          </div>

          <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
             <label className="text-xs font-bold uppercase tracking-wider text-gray-500 block mb-1">{t('operator.allKitsPage.view.quantity', { defaultValue: 'Quantity' })}</label>
            <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {viewModal.quantity || 1}
            </p>
          </div>
        </div>

        {/* Location Section */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-blue-500">
            <MapPin size={20} />
            <h3 className="font-bold">{t('operator.allKitsPage.view.deploymentDetails', { defaultValue: 'Deployment Details' })}</h3>
          </div>
          <div className={`p-5 rounded-xl border ${theme === 'dark' ? 'border-gray-800 bg-gray-900/30' : 'border-gray-100 bg-white'}`}>
                    <p className={`text-lg ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
              {(() => {
                // Try deploymentLocation first
                if (viewModal.deploymentLocation) {
                  return viewModal.deploymentLocation;
                }
                
                // Then try location object
                if (viewModal.location && typeof viewModal.location === 'object') {
                  const loc = viewModal.location;
                  const parts = [];
                  if (loc.address) parts.push(loc.address);
                  if (loc.city) parts.push(loc.city);
                  if (loc.state) parts.push(loc.state);
                  if (loc.country) parts.push(loc.country);
                  return parts.join(', ') || t('operator.allKitsPage.view.noAddress', { defaultValue: 'No address provided' });
                }
                
                // Fallback to string location
                if (typeof viewModal.location === 'string') {
                  return viewModal.location;
                }
                
                return t('operator.allKitsPage.view.noAddress', { defaultValue: 'No address provided' });
              })()}
            </p>
            
            {(() => {
              const loc = viewModal.location;
              const coords = (typeof loc === 'object' && loc?.coordinates) || 
                            viewModal.coordinates;
              
              if (coords && (coords.lat !== 0 || coords.lng !== 0)) {
                return (
                  <div className={`mt-4 flex gap-6 text-sm font-mono border-t pt-4 ${
                    theme === 'dark' ? 'text-gray-400 border-gray-800' : 'text-gray-600 border-gray-200'
                  }`}>
                    <span>LAT: {coords.lat || t('operator.allKitsPage.common.na', { defaultValue: 'N/A' })}</span>
                    <span>LNG: {coords.lng || t('operator.allKitsPage.common.na', { defaultValue: 'N/A' })}</span>
                  </div>
                );
              }
              
              return (
                <div className={`mt-4 text-sm border-t pt-4 ${
                  theme === 'dark' ? 'text-gray-500 border-gray-800' : 'text-gray-400 border-gray-200'
                }`}>
                  {t('operator.allKitsPage.view.coordinatesNotSet', { defaultValue: 'Coordinates not set - Location will be geocoded on activation' })}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Device Specification */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-purple-500">
            <Settings size={20} />
            <h3 className="font-bold">{t('operator.allKitsPage.view.deviceSpecification', { defaultValue: 'Device Specification' })}</h3>
          </div>
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 rounded-xl ${theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
            {/* Brand / Model */}
            <div>
              <label className="text-xs text-gray-500 block mb-1">{t('operator.allKitsPage.view.brand', { defaultValue: 'Brand' })}</label>
              <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {viewModal.deviceBrand || t('operator.allKitsPage.view.pendingConfiguration', { defaultValue: 'Pending Configuration' })}
              </p>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">{t('operator.allKitsPage.view.model', { defaultValue: 'Model' })}</label>
              <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {viewModal.deviceModel || t('operator.allKitsPage.view.pendingConfiguration', { defaultValue: 'Pending Configuration' })}
              </p>
            </div>

            <div>
              <label className="text-xs text-gray-500 block mb-1">{t('operator.allKitsPage.view.activationDate', { defaultValue: 'Activation Date' })}</label>
              <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {(() => {
                  const activationDate = viewModal.activationDate || viewModal.createdAt;
                  if (activationDate) {
                    return formatDate(activationDate);
                  }
                  return viewModal.status === 'pending' ? t('operator.allKitsPage.view.pendingActivation', { defaultValue: 'Pending Activation' }) : t('operator.allKitsPage.common.na', { defaultValue: 'N/A' });
                })()}
              </p>
            </div>

            {/* Network Coverage */}
            <div>
              <label className="text-xs text-gray-500 block mb-1">{t('operator.allKitsPage.view.coverageRadius', { defaultValue: 'Coverage Radius' })}</label>
              <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {viewModal.networkCoverageRadius || 100} {t('operator.allKitsPage.view.meters', { defaultValue: 'meters' })}
              </p>
            </div>

            <div className="col-span-2 mt-2">
              <label className="text-xs text-gray-500 block mb-1">{t('operator.allKitsPage.view.serialNumber', { defaultValue: 'Serial Number' })}</label>
              <p className={`font-mono text-sm ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                {viewModal.serialNumber || t('operator.allKitsPage.common.na', { defaultValue: 'N/A' })}
              </p>
            </div>

            <div className="col-span-2">
              <label className="text-xs text-gray-500 block mb-1">{t('operator.allKitsPage.view.macAddress', { defaultValue: 'MAC Address' })}</label>
              <p className={`font-mono text-sm ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                {viewModal.macAddress || t('operator.allKitsPage.common.na', { defaultValue: 'N/A' })}
              </p>
            </div>
          </div>
        </div>

        {/* Payment & KYC Status */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-green-500">
            <Wifi size={20} />
            <h3 className="font-bold">{t('operator.allKitsPage.view.statusInformation', { defaultValue: 'Status Information' })}</h3>
          </div>
          <div className={`grid grid-cols-2 gap-4 p-5 rounded-xl ${theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
            <div>
              <label className="text-xs text-gray-500 block mb-1">{t('operator.allKitsPage.view.paymentStatus', { defaultValue: 'Payment Status' })}</label>
              <p className={`font-medium capitalize ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {viewModal.paymentStatus || t('operator.allKitsPage.common.pending', { defaultValue: 'Pending' })}
              </p>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">{t('operator.allKitsPage.view.kycStatus', { defaultValue: 'KYC Status' })}</label>
              <p className={`font-medium capitalize ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {viewModal.kycStatus || t('operator.allKitsPage.view.notRequired', { defaultValue: 'Not Required' })}
              </p>
            </div>
          </div>
        </div>

        {/* Timestamps */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 rounded-xl border ${theme === 'dark' ? 'bg-gray-900/30 border-gray-800' : 'bg-gray-50 border-gray-100'}`}>
          <div>
            <label className="text-xs text-gray-500 block mb-1">{t('operator.allKitsPage.view.created', { defaultValue: 'Created' })}</label>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              {formatDate(viewModal.createdAt)}
            </p>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">{t('operator.allKitsPage.view.lastUpdated', { defaultValue: 'Last Updated' })}</label>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              {formatDate(viewModal.updatedAt)}
            </p>
          </div>
        </div>

      </div>
    </div>
  </div>
)}
      {editModal && (
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
    <div className={`rounded-xl max-w-2xl w-full shadow-2xl relative overflow-hidden ${theme === 'dark' ? 'bg-[#1a1a1b] border border-gray-800' : 'bg-white'}`}>
      
      {editSuccess && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500 animate-bounce" />
            <h3 className="text-2xl font-bold text-white">{t('operator.allKitsPage.messages.kitUpdatedTitle', { defaultValue: 'Kit Updated!' })}</h3>
          </div>
        </div>
      )}

      <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
        <h2 className={`text-xl sm:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{t('operator.allKitsPage.edit.manageKitConfiguration', { defaultValue: 'Manage Kit Configuration' })}</h2>
        <button onClick={() => setEditModal(null)} className="text-gray-400 hover:text-white"><X size={24} /></button>
      </div>

      <form onSubmit={handleUpdateKit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Kit Type Toggle */}
        <div className="space-y-3">
          <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{t('operator.allKitsPage.edit.selectKitType', { defaultValue: 'Select Kit Type' })}</label>
          <div className="grid grid-cols-3 gap-4">
  {(['basic', 'advanced', 'premium'] as const).map((type) => (
    <button
      key={type}
      type="button"
      onClick={() => setEditForm({ ...editForm, kitType: type })}
      className={`py-3 rounded-lg border capitalize transition-all ${
        editForm.kitType === type
          ? 'bg-[#2E5A88] border-[#2E5A88] text-white shadow-lg'
          : theme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500' : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-[#2E5A88]'
      }`}
    >
      {type}
    </button>
  ))}
</div>
        </div>

        {/* Quantity */}
        <div className="space-y-2">
          <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{t('operator.allKitsPage.view.quantity', { defaultValue: 'Quantity' })}</label>
          <div className="relative">
            <Settings className="absolute left-3 top-3.5 text-gray-500" size={18} />
            <input
              type="number"
              value={editForm.quantity}
              onChange={(e) => setEditForm({ ...editForm, quantity: parseInt(e.target.value) || 0 })}
              className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#2E5A88]/30 focus:border-[#2E5A88] outline-none transition ${
                theme === 'dark' ? 'bg-[#111] border-gray-700 text-white' : 'bg-white border-gray-300'
              }`}
            />
          </div>
        </div>

        {/* Deployment Location */}
        <div className="space-y-2">
          <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{t('operator.allKitsPage.columns.location', { defaultValue: 'Location' })}</label>
          <div className="relative">
            <MapPin className="absolute left-3 top-3.5 text-gray-500" size={18} />
            <input
              type="text"
              placeholder={t('operator.allKitsPage.edit.enterAddressOrCoordinates', { defaultValue: 'Enter address or coordinates' })}
              value={editForm.deploymentLocation}
              onChange={(e) => setEditForm({ ...editForm, deploymentLocation: e.target.value })}
              className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#2E5A88]/30 focus:border-[#2E5A88] outline-none transition ${
                theme === 'dark' ? 'bg-[#111] border-gray-700 text-white' : 'bg-white border-gray-300'
              }`}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
          <button
            type="button"
            onClick={() => setEditModal(null)}
            className={`w-full sm:flex-1 px-6 py-3 rounded-lg font-medium transition ${
              theme === 'dark' ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t('operator.allKitsPage.actions.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="submit"
            disabled={actionLoading}
            className="w-full sm:flex-1 px-6 py-3 rounded-lg font-semibold bg-[#2E5A88] hover:bg-[#244a73] text-white transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('operator.allKitsPage.actions.updateKit', { defaultValue: 'Update Kit' })}
          </button>
        </div>
      </form>
    </div>
  </div>
)}
      </div>
    </>
  );
}
